import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Heart,
  Users,
  Calendar,
  FileText,
  Activity,
  DollarSign,
  MessageSquare,
  Settings,
  Eye,
  Edit,
  Mail,
  MoreVertical,
  Clock,
  CheckCircle,
  AlertTriangle,
  TrendingUp,
  Briefcase,
  Phone,
  MapPin,
} from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { Avatar } from '@/components/ui/Avatar';

/**
 * AdminFamilyDetailPage - Deep dive into a specific family
 * 
 * Shows:
 * - Family overview and stats
 * - All children with health summaries
 * - Family members and permissions
 * - Care team connections
 * - Documents and expenses
 * - Activity timeline
 * - Attorney connections (if any)
 */
export default function AdminFamilyDetailPage() {
  const { familyId } = useParams();
  const [activeTab, setActiveTab] = useState('overview');

  // Mock data - would come from API
  const family = {
    id: familyId,
    name: 'Johnson Family',
    status: 'active',
    plan: 'premium',
    createdAt: '2024-06-15',
    primaryContact: {
      name: 'Sarah Johnson',
      email: 'sarah.johnson@email.com',
      phone: '(555) 123-4567',
    },
    address: '123 Main Street, San Francisco, CA 94102',
    members: [
      { id: 'm1', name: 'Sarah Johnson', role: 'family_admin', email: 'sarah.johnson@email.com', lastActive: '2 hours ago' },
      { id: 'm2', name: 'David Johnson', role: 'family_member', email: 'david.j@email.com', lastActive: '1 day ago' },
      { id: 'm3', name: 'Grandma Rose', role: 'family_member', email: 'rose.j@email.com', lastActive: '3 days ago' },
      { id: 'm4', name: 'Aunt Mary', role: 'family_member', email: 'mary.j@email.com', lastActive: '1 week ago' },
    ],
    children: [
      { 
        id: 'c1', 
        name: 'Emma Johnson', 
        age: 7, 
        diagnoses: ['Cerebral Palsy', 'Epilepsy'],
        healthScore: 85,
        recentLogs: 12,
        careTeamSize: 8,
      },
      { 
        id: 'c2', 
        name: 'Jack Johnson', 
        age: 4, 
        diagnoses: ['HIE'],
        healthScore: 72,
        recentLogs: 8,
        careTeamSize: 5,
      },
    ],
    stats: {
      healthLogs: 156,
      documents: 34,
      expenses: '$12,450',
      appointments: 28,
      careTeam: 12,
      goals: 8,
    },
    attorneyConnection: {
      name: 'Law Offices of Smith & Associates',
      attorney: 'Michael Smith',
      caseStatus: 'active',
      connectedSince: '2024-07-01',
    },
    recentActivity: [
      { id: 'a1', type: 'health_log', description: 'Added seizure log for Emma', time: '2 hours ago', user: 'Sarah' },
      { id: 'a2', type: 'document', description: 'Uploaded MRI report', time: '5 hours ago', user: 'Sarah' },
      { id: 'a3', type: 'appointment', description: 'Scheduled neurology follow-up', time: '1 day ago', user: 'David' },
      { id: 'a4', type: 'expense', description: 'Added therapy expense $250', time: '2 days ago', user: 'Sarah' },
    ],
  };

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <Link 
        to="/admin/families" 
        className="inline-flex items-center gap-2 text-warm-gray hover:text-navy transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Families
      </Link>

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 rounded-2xl bg-sage/10 flex items-center justify-center">
            <Heart className="w-8 h-8 text-sage" />
          </div>
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-display-sm text-navy">{family.name}</h1>
              <Badge className="bg-sage/10 text-sage">{family.status}</Badge>
              <Badge className="bg-navy/10 text-navy">{family.plan}</Badge>
            </div>
            <p className="text-warm-gray">
              Member since {new Date(family.createdAt).toLocaleDateString()}
            </p>
            <div className="flex items-center gap-4 mt-2 text-sm text-warm-gray">
              <span className="flex items-center gap-1">
                <Mail className="w-4 h-4" />
                {family.primaryContact.email}
              </span>
              <span className="flex items-center gap-1">
                <Phone className="w-4 h-4" />
                {family.primaryContact.phone}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Link to={`/admin/families/${familyId}/view-as`}>
            <Button variant="outline">
              <Eye className="w-4 h-4 mr-2" />
              View as Family
            </Button>
          </Link>
          <Button variant="outline">
            <Mail className="w-4 h-4 mr-2" />
            Contact
          </Button>
          <Button variant="primary">
            <Edit className="w-4 h-4 mr-2" />
            Edit
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <QuickStat icon={Activity} label="Health Logs" value={family.stats.healthLogs} />
        <QuickStat icon={FileText} label="Documents" value={family.stats.documents} />
        <QuickStat icon={DollarSign} label="Expenses" value={family.stats.expenses} />
        <QuickStat icon={Calendar} label="Appointments" value={family.stats.appointments} />
        <QuickStat icon={Users} label="Care Team" value={family.stats.careTeam} />
        <QuickStat icon={CheckCircle} label="Goals" value={family.stats.goals} />
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="children">Children ({family.children.length})</TabsTrigger>
          <TabsTrigger value="members">Members ({family.members.length})</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="expenses">Expenses</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Children Summary */}
            <Card className="p-6 lg:col-span-2">
              <h3 className="font-display text-heading-md text-navy mb-4">Children</h3>
              <div className="space-y-4">
                {family.children.map((child) => (
                  <div 
                    key={child.id}
                    className="flex items-center justify-between p-4 rounded-lg bg-cream/50 hover:bg-cream transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <Avatar className="w-12 h-12 bg-sage/20 text-sage">
                        {child.name.charAt(0)}
                      </Avatar>
                      <div>
                        <p className="font-medium text-navy">{child.name}</p>
                        <p className="text-sm text-warm-gray">
                          {child.age} years • {child.diagnoses.join(', ')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-6 text-sm">
                      <div className="text-center">
                        <p className="text-2xl font-display font-semibold text-sage">{child.healthScore}</p>
                        <p className="text-xs text-warm-gray">Health Score</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-semibold text-navy">{child.recentLogs}</p>
                        <p className="text-xs text-warm-gray">Recent Logs</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-semibold text-navy">{child.careTeamSize}</p>
                        <p className="text-xs text-warm-gray">Care Team</p>
                      </div>
                      <Link 
                        to={`/admin/families/${familyId}/children/${child.id}`}
                        className="p-2 rounded-lg hover:bg-sage/10 text-sage"
                      >
                        <Eye className="w-5 h-5" />
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Attorney Connection */}
            <Card className="p-6">
              <h3 className="font-display text-heading-md text-navy mb-4">Attorney Connection</h3>
              {family.attorneyConnection ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-navy/10 flex items-center justify-center">
                      <Briefcase className="w-5 h-5 text-navy" />
                    </div>
                    <div>
                      <p className="font-medium text-navy">{family.attorneyConnection.attorney}</p>
                      <p className="text-sm text-warm-gray">{family.attorneyConnection.name}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between py-2 border-t border-warm-gray/10">
                    <span className="text-sm text-warm-gray">Case Status</span>
                    <Badge className="bg-sage/10 text-sage">{family.attorneyConnection.caseStatus}</Badge>
                  </div>
                  <div className="flex items-center justify-between py-2 border-t border-warm-gray/10">
                    <span className="text-sm text-warm-gray">Connected Since</span>
                    <span className="text-sm text-navy">
                      {new Date(family.attorneyConnection.connectedSince).toLocaleDateString()}
                    </span>
                  </div>
                  <Link 
                    to={`/admin/attorneys/cases/${family.attorneyConnection.caseStatus}`}
                    className="block text-center text-sm text-sage hover:text-sage-dark font-medium pt-2"
                  >
                    View Case Details →
                  </Link>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Briefcase className="w-10 h-10 text-warm-gray/30 mx-auto mb-2" />
                  <p className="text-sm text-warm-gray">No attorney connection</p>
                </div>
              )}
            </Card>

            {/* Recent Activity */}
            <Card className="p-6 lg:col-span-2">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display text-heading-md text-navy">Recent Activity</h3>
                <Link to="#" className="text-sm text-sage hover:text-sage-dark">View All</Link>
              </div>
              <div className="space-y-4">
                {family.recentActivity.map((activity) => (
                  <div key={activity.id} className="flex items-start gap-4">
                    <div className={`p-2 rounded-lg ${
                      activity.type === 'health_log' ? 'bg-sage/10 text-sage' :
                      activity.type === 'document' ? 'bg-navy/10 text-navy' :
                      activity.type === 'appointment' ? 'bg-terracotta/10 text-terracotta' :
                      'bg-warm-gray/10 text-warm-gray'
                    }`}>
                      {activity.type === 'health_log' ? <Activity className="w-4 h-4" /> :
                       activity.type === 'document' ? <FileText className="w-4 h-4" /> :
                       activity.type === 'appointment' ? <Calendar className="w-4 h-4" /> :
                       <DollarSign className="w-4 h-4" />}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-navy">{activity.description}</p>
                      <p className="text-xs text-warm-gray">
                        by {activity.user} • {activity.time}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Account Settings */}
            <Card className="p-6">
              <h3 className="font-display text-heading-md text-navy mb-4">Account</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-warm-gray">Plan</span>
                  <Badge className="bg-navy/10 text-navy">{family.plan}</Badge>
                </div>
                <div className="flex items-center justify-between py-2 border-t border-warm-gray/10">
                  <span className="text-sm text-warm-gray">Status</span>
                  <Badge className="bg-sage/10 text-sage">{family.status}</Badge>
                </div>
                <div className="flex items-center justify-between py-2 border-t border-warm-gray/10">
                  <span className="text-sm text-warm-gray">Address</span>
                  <span className="text-sm text-navy text-right max-w-[200px]">{family.address}</span>
                </div>
                <div className="pt-4 space-y-2">
                  <Button variant="outline" size="sm" className="w-full">
                    <Settings className="w-4 h-4 mr-2" />
                    Manage Settings
                  </Button>
                  <Button variant="outline" size="sm" className="w-full text-warning">
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    Suspend Account
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="children" className="mt-6">
          <Card className="p-6">
            <h3 className="font-display text-heading-md text-navy mb-4">Children Details</h3>
            <p className="text-warm-gray">Detailed children management coming soon...</p>
          </Card>
        </TabsContent>

        <TabsContent value="members" className="mt-6">
          <Card className="p-6">
            <h3 className="font-display text-heading-md text-navy mb-4">Family Members</h3>
            <div className="space-y-4">
              {family.members.map((member) => (
                <div 
                  key={member.id}
                  className="flex items-center justify-between p-4 rounded-lg border border-warm-gray/10"
                >
                  <div className="flex items-center gap-4">
                    <Avatar className="w-10 h-10">{member.name.charAt(0)}</Avatar>
                    <div>
                      <p className="font-medium text-navy">{member.name}</p>
                      <p className="text-sm text-warm-gray">{member.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <Badge className={member.role === 'family_admin' ? 'bg-navy/10 text-navy' : 'bg-warm-gray/10 text-warm-gray'}>
                      {member.role.replace('_', ' ')}
                    </Badge>
                    <span className="text-sm text-warm-gray">{member.lastActive}</span>
                    <Button variant="ghost" size="sm">
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="documents" className="mt-6">
          <Card className="p-6">
            <h3 className="font-display text-heading-md text-navy mb-4">Documents</h3>
            <p className="text-warm-gray">Document browser coming soon...</p>
          </Card>
        </TabsContent>

        <TabsContent value="expenses" className="mt-6">
          <Card className="p-6">
            <h3 className="font-display text-heading-md text-navy mb-4">Expenses</h3>
            <p className="text-warm-gray">Expense management coming soon...</p>
          </Card>
        </TabsContent>

        <TabsContent value="activity" className="mt-6">
          <Card className="p-6">
            <h3 className="font-display text-heading-md text-navy mb-4">Activity Log</h3>
            <p className="text-warm-gray">Full activity timeline coming soon...</p>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function QuickStat({ 
  icon: Icon, 
  label, 
  value 
}: { 
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string | number;
}) {
  return (
    <Card className="p-4">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-sage/10">
          <Icon className="w-4 h-4 text-sage" />
        </div>
        <div>
          <p className="text-lg font-display font-semibold text-navy">{value}</p>
          <p className="text-xs text-warm-gray">{label}</p>
        </div>
      </div>
    </Card>
  );
}
